/** The newest creation runs: one running shows its progress; a finished one says what it made and what went wrong. */

import { useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useKnowledgeJobs } from '@/hooks/use-knowledge-promotion';
import type { KnowledgeJob } from './types';

const titleOf = (job: KnowledgeJob): string => (job.kind === 'article' ? `From "${job.articleName ?? 'article'}"` : 'Imported pairs');

const countsOf = (job: KnowledgeJob): string =>
	[
		`${job.created} created`,
		job.skipped ? `${job.skipped} already there` : null,
		job.failed ? `${job.failed} not created` : null,
		job.status === 'running' && job.total ? `${job.created + job.skipped + job.failed} of ${job.total}` : null
	]
		.filter(Boolean)
		.join(' · ');

/** `articleId` limits the banner to the runs generating from that source. */
export const JobBanner = ({ articleId }: { articleId?: string } = {}) => {
	const { data: jobs } = useKnowledgeJobs();
	const [dismissed, setDismissed] = useState<string[]>([]);
	const recent = (jobs ?? [])
		.filter((job) => !articleId || job.articleId === articleId)
		.filter((job) => !dismissed.includes(job.id))
		.filter((job) => job.status === 'running' || Date.now() - new Date(job.finishedAt ?? job.createdAt).getTime() < 30 * 60 * 1000)
		.slice(0, 3);
	if (!recent.length) return null;
	return (
		<div className='flex flex-col gap-1.5' data-testid='knowledge-jobs'>
			{recent.map((job) => (
				<div key={job.id} className='flex items-start gap-2 rounded-md border px-3 py-2 text-xs'>
					{job.status === 'running' ? (
						<Loader2 className='mt-0.5 size-3.5 shrink-0 animate-spin' aria-hidden />
					) : job.status === 'done' && !job.failed ? (
						<CheckCircle2 className='mt-0.5 size-3.5 shrink-0 text-green-700' aria-hidden />
					) : (
						<AlertTriangle className='mt-0.5 size-3.5 shrink-0 text-amber-600' aria-hidden />
					)}
					<div className='flex min-w-0 flex-1 flex-col gap-0.5'>
						<span className='font-medium'>
							{titleOf(job)}
							{job.status === 'running' ? ': working…' : job.status === 'failed' ? ': stopped' : ': done'}
						</span>
						<span className='text-muted-foreground'>{countsOf(job)}</span>
						{job.problems.slice(0, 3).map((problem) => (
							<span key={problem} className='text-[11px] text-amber-700'>
								{problem}
							</span>
						))}
					</div>
					{job.status !== 'running' && (
						<Button
							variant='ghost'
							size='icon'
							className='size-6'
							aria-label='Dismiss'
							onClick={() => setDismissed((ids) => [...ids, job.id])}
						>
							<X className='size-3.5' />
						</Button>
					)}
				</div>
			))}
		</div>
	);
};
